import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Calendar,
  FileText,
  Users,
  Bell,
  XCircle,
  Plus,
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';

const AddComplianceReminder = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    dueDate: '',
    priority: '',
    assignedTo: '',
    estimatedTime: '',
    requiredDocuments: '',
    reminderFrequency: 'once',
    reminderDays: [7, 3, 1], // Days before due date
    status: 'pending',
    tags: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { value: 'statutory', label: 'Statutory' },
    { value: 'tax', label: 'Tax' },
    { value: 'license', label: 'License' },
    { value: 'safety', label: 'Safety' },
    { value: 'audit', label: 'Audit' },
    { value: 'data', label: 'Data' },
    { value: 'hr', label: 'HR' },
    { value: 'legal', label: 'Legal' }
  ];

  const priorities = [
    { value: 'high', label: 'High', color: 'text-red-600 bg-red-50' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600 bg-yellow-50' },
    { value: 'low', label: 'Low', color: 'text-green-600 bg-green-50' }
  ];

  const assignedTeams = [
    { value: 'hr_team', label: 'HR Team' },
    { value: 'finance_team', label: 'Finance Team' },
    { value: 'payroll_team', label: 'Payroll Team' },
    { value: 'legal_team', label: 'Legal Team' },
    { value: 'safety_officer', label: 'Safety Officer' },
    { value: 'it_team', label: 'IT Team' },
    { value: 'admin_team', label: 'Admin Team' },
    { value: 'compliance_team', label: 'Compliance Team' }
  ];

  const reminderFrequencies = [
    { value: 'once', label: 'One Time' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' },
    { value: 'custom', label: 'Custom' }
  ];

  // Helper function for text input key press
  const handleTextKeyPress = (e) => {
    // Only allow alphanumeric, spaces, and commas
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Helper function for number input key down
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;
    
    // Apply validation based on field type
    if (field === 'title') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 100);
    } else if (field === 'description') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 1000);
    } else if (field === 'estimatedTime') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 50);
    } else if (field === 'requiredDocuments') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleDocumentsChange = (value) => {
    const processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
    const documents = processedValue.split(',').map(doc => doc.trim()).filter(doc => doc);
    setFormData(prev => ({
      ...prev,
      requiredDocuments: processedValue,
      documentsArray: documents
    }));
  };

  const handleReminderDaysChange = (days) => {
    setFormData(prev => ({
      ...prev,
      reminderDays: days
    }));
  };

  const addReminderDay = () => {
    const input = prompt('Enter number of days before due date:');
    if (input) {
      const newDay = parseInt(input.replace(/\D/g, ''));
      if (newDay && newDay > 0 && newDay <= 365 && !formData.reminderDays.includes(newDay)) {
        setFormData(prev => ({
          ...prev,
          reminderDays: [...prev.reminderDays, newDay].sort((a, b) => b - a)
        }));
      } else if (newDay > 365) {
        alert('Reminder days cannot exceed 365 days');
      }
    }
  };

  const removeReminderDay = (dayToRemove) => {
    setFormData(prev => ({
      ...prev,
      reminderDays: prev.reminderDays.filter(day => day !== dayToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.title)) {
      newErrors.title = 'Title can only contain alphanumeric characters, spaces, and commas';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must not exceed 100 characters';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.description)) {
      newErrors.description = 'Description can only contain alphanumeric characters, spaces, and commas';
    } else if (formData.description.length > 1000) {
      newErrors.description = 'Description must not exceed 1000 characters';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    } else {
      const dueDate = new Date(formData.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dueDate < today) {
        newErrors.dueDate = 'Due date cannot be in the past';
      }
    }

    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }

    if (!formData.assignedTo) {
      newErrors.assignedTo = 'Assigned team is required';
    }

    if (formData.estimatedTime && !/^[a-zA-Z0-9\s,]+$/.test(formData.estimatedTime)) {
      newErrors.estimatedTime = 'Estimated time can only contain alphanumeric characters, spaces, and commas';
    } else if (formData.estimatedTime && formData.estimatedTime.length > 50) {
      newErrors.estimatedTime = 'Estimated time must not exceed 50 characters';
    }

    if (formData.requiredDocuments && !/^[a-zA-Z0-9\s,]*$/.test(formData.requiredDocuments)) {
      newErrors.requiredDocuments = 'Required documents can only contain alphanumeric characters, spaces, and commas';
    } else if (formData.requiredDocuments && formData.requiredDocuments.length > 500) {
      newErrors.requiredDocuments = 'Required documents must not exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the reminder data
      const reminderData = {
        ...formData,
        id: `REM-${Date.now()}`, // Generate unique ID
        createdAt: new Date().toISOString(),
        createdBy: 'Current User', // In real app, get from auth context
        status: 'pending',
        daysLeft: Math.ceil((new Date(formData.dueDate) - new Date()) / (1000 * 60 * 60 * 24)),
        documents: formData.requiredDocuments.split(',').map(doc => doc.trim()).filter(doc => doc),
        tags: formData.tags || [],
        history: [
          {
            date: new Date().toISOString(),
            action: 'Created',
            user: 'Current User',
            notes: 'Reminder created successfully'
          }
        ]
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Show success message with details
      alert(`✅ Compliance reminder "${formData.title}" created successfully!\n\nDue Date: ${new Date(formData.dueDate).toLocaleDateString()}\nPriority: ${formData.priority}\nAssigned To: ${formData.assignedTo}`);
      
      // Navigate back to compliance reminders
      navigate('/compliance_reminders');
      
    } catch (error) {

      alert('❌ Error creating reminder. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/compliance_reminders');
  };

  const getPriorityColor = (priority) => {
    const priorityObj = priorities.find(p => p.value === priority);
    return priorityObj ? priorityObj.color : 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Compliance Reminder</h1>
            <p className="text-gray-600">Create a new compliance reminder and deadline</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleBack}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={isSubmitting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="h-4 w-4" />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}
          >
            {isSubmitting ? 'Creating Reminder...' : 'Create Reminder'}
          </Button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="h-5 w-5 text-[#0F2942]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                <p className="text-sm text-gray-600">Enter the basic details of the compliance reminder</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  onKeyPress={handleTextKeyPress}
                  maxLength={100}
                  required
                  className={`w-full border rounded-md px-3 py-2 text-sm ${
                    errors.title ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50`}
                  placeholder="Enter reminder title"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.title.length}/100 characters
                </p>
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm ${
                    errors.category ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50`}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                onKeyPress={handleTextKeyPress}
                maxLength={3000}
                required
                className={`w-full border rounded-md px-3 py-2 text-sm ${
                  errors.description ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                } focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50`}
                placeholder="Enter detailed description of the compliance requirement"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/1000 characters
              </p>
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
            </div>
          </div>

          {/* Schedule & Priority */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-50 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Schedule & Priority</h2>
                <p className="text-sm text-gray-600">Set deadlines and priority levels</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date *
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm ${
                    errors.dueDate ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50`}
                />
                {errors.dueDate && <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority *
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm ${
                    errors.priority ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50`}
                >
                  <option value="">Select priority</option>
                  {priorities.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
                {errors.priority && <p className="mt-1 text-sm text-red-600">{errors.priority}</p>}
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reminder Frequency
              </label>
              <select
                value={formData.reminderFrequency}
                onChange={(e) => handleInputChange('reminderFrequency', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
              >
                {reminderFrequencies.map(freq => (
                  <option key={freq.value} value={freq.value}>
                    {freq.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignment & Resources */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assignment & Resources</h2>
                <p className="text-sm text-gray-600">Assign responsibility and estimate resources</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To *
                </label>
                <select
                  value={formData.assignedTo}
                  onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm ${
                    errors.assignedTo ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50`}
                >
                  <option value="">Select team</option>
                  {assignedTeams.map(team => (
                    <option key={team.value} value={team.value}>
                      {team.label}
                    </option>
                  ))}
                </select>
                {errors.assignedTo && <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Time
                </label>
                <input
                  type="text"
                  value={formData.estimatedTime}
                  onChange={(e) => handleInputChange('estimatedTime', e.target.value)}
                  onKeyPress={handleTextKeyPress}
                  maxLength={50}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                  placeholder="e.g., 4 hours, 2 days"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.estimatedTime.length}/50 characters
                </p>
              </div>
            </div>
          </div>

          {/* Documents & Notifications */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Bell className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Documents & Notifications</h2>
                <p className="text-sm text-gray-600">Specify required documents and notification settings</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Documents
                </label>
                <input
                  type="text"
                  value={formData.requiredDocuments}
                  onChange={(e) => handleDocumentsChange(e.target.value)}
                  onKeyPress={handleTextKeyPress}
                  maxLength={500}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-[#0F2942]/50"
                  placeholder="Enter documents separated by commas (e.g., PF Register, Salary Register, Bank Statement)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.requiredDocuments.length}/500 characters
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Separate multiple documents with commas
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder Notifications
                </label>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Send reminders before due date:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.reminderDays.map(day => (
                      <span
                        key={day}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {day} day{day !== 1 ? 's' : ''} before
                        <button
                          type="button"
                          onClick={() => removeReminderDay(day)}
                          className="ml-2 text-[#0F2942] hover:text-blue-800"
                        >
                          <XCircle className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={addReminderDay}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Preview Section */}
      {formData.title && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="text-lg font-semibold text-gray-900">{formData.title}</h4>
                  {formData.priority && (
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(formData.priority)}`}>
                      {formData.priority.toUpperCase()}
                    </span>
                  )}
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                    PENDING
                  </span>
                </div>
                {formData.description && (
                  <p className="text-sm text-gray-600 mb-3">{formData.description}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  {formData.dueDate && (
                    <div>
                      <p className="text-gray-500">Due Date</p>
                      <p className="font-medium text-gray-900">{formData.dueDate}</p>
                    </div>
                  )}
                  {formData.assignedTo && (
                    <div>
                      <p className="text-gray-500">Assigned To</p>
                      <p className="font-medium text-gray-900">
                        {assignedTeams.find(t => t.value === formData.assignedTo)?.label}
                      </p>
                    </div>
                  )}
                  {formData.estimatedTime && (
                    <div>
                      <p className="text-gray-500">Estimated Time</p>
                      <p className="font-medium text-gray-900">{formData.estimatedTime}</p>
                    </div>
                  )}
                  {formData.category && (
                    <div>
                      <p className="text-gray-500">Category</p>
                      <p className="font-medium text-gray-900">
                        {categories.find(c => c.value === formData.category)?.label}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddComplianceReminder;
